import { useEffect, useRef, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { FiAlertTriangle } from 'react-icons/fi';
import Input from '../atoms/Input';
import RadioButton from '../atoms/RadioButton';
import config from '../../config/config';
import detectEmailTypo from '../../utils/detectEmailTypo';
import { CheckoutFormData } from './checkoutValidation';

const EMAIL_CONFIRM_ERROR_DEBOUNCE_MS = 500;
const EMAIL_TYPO_DEBOUNCE_MS = 400;

interface CheckoutContactFormProps {
  form: UseFormReturn<CheckoutFormData>;
  showPhoneOption: boolean;
  isBackoffice: boolean;
  disabled?: boolean;
}

/**
 * CheckoutContactForm - Contact method selection with email/phone inputs
 *
 * Theme Support: ✅ Full light/dark mode support
 * Colors Used: Uses semantic colors via Input and RadioButton components
 */
const CheckoutContactForm: React.FC<CheckoutContactFormProps> = ({
  form,
  showPhoneOption,
  isBackoffice,
  disabled = false,
}) => {
  const {
    control, formState, getValues, setValue, clearErrors, watch, trigger,
  } = form;

  const [debouncedEmailConfirmError, setDebouncedEmailConfirmError] = useState<string | undefined>();
  const [emailTypoSuggestion, setEmailTypoSuggestion] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typoDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emailValue = watch('email');
  const emailConfirmValue = watch('emailConfirm');

  useEffect(() => {
    setDebouncedEmailConfirmError(undefined);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const rawError = formState.errors.emailConfirm?.message;
    if (rawError) {
      debounceRef.current = setTimeout(() => {
        setDebouncedEmailConfirmError(rawError);
        debounceRef.current = null;
      }, EMAIL_CONFIRM_ERROR_DEBOUNCE_MS);
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [formState.errors.emailConfirm?.message, emailConfirmValue]);

  useEffect(() => {
    if (typoDebounceRef.current) clearTimeout(typoDebounceRef.current);

    // Soft warning only — don't compete with hard validation errors
    if (!emailValue || formState.errors.email) {
      setEmailTypoSuggestion(null);
      return undefined;
    }

    typoDebounceRef.current = setTimeout(() => {
      setEmailTypoSuggestion(detectEmailTypo(emailValue));
      typoDebounceRef.current = null;
    }, EMAIL_TYPO_DEBOUNCE_MS);

    return () => {
      if (typoDebounceRef.current) clearTimeout(typoDebounceRef.current);
    };
  }, [emailValue, formState.errors.email]);

  const applyEmailSuggestion = (suggestion: string) => {
    const previousEmail = getValues('email');
    setValue('email', suggestion, { shouldDirty: true, shouldValidate: true });
    if (getValues('emailConfirm') === previousEmail) {
      setValue('emailConfirm', suggestion, { shouldDirty: true, shouldValidate: true });
    } else {
      trigger('emailConfirm');
    }
    setEmailTypoSuggestion(null);
  };

  const renderEmailField = () => (
    <>
      <Controller
        name="email"
        control={control}
        render={({ field }) => (
          <Input
            label="Correo electrónico"
            id="email"
            {...field}
            onChange={(e) => field.onChange(e.target.value.replace(/\s/g, ''))}
            errMsg={formState.errors.email?.message}
            labelAbove
          />
        )}
      />
      {emailTypoSuggestion && (
        <button
          type="button"
          onClick={() => applyEmailSuggestion(emailTypoSuggestion)}
          className="flex items-start gap-2 self-stretch rounded-xl border border-warning-500/40 bg-warning-100 px-3 py-2.5 text-left transition-opacity hover:opacity-90"
        >
          <FiAlertTriangle className="mt-0.5 shrink-0 text-base text-warning-600" aria-hidden />
          <span className="text-sm text-warning-700">
            ¿Quisiste decir{' '}
            <span className="font-semibold underline underline-offset-2">{emailTypoSuggestion}</span>
            ? Tocá para corregir.
          </span>
        </button>
      )}
    </>
  );

  const renderEmailConfirmField = () => (
    <Controller
      name="emailConfirm"
      control={control}
      render={({ field }) => (
        <Input
          label="Confirmar correo electrónico"
          id="emailConfirm"
          {...field}
          onChange={(e) => field.onChange(e.target.value.replace(/\s/g, ''))}
          autoComplete={
            config.production && !isBackoffice
              ? 'idontwantthistoeverautocompleteokok??'
              : 'email'
          }
          onPaste={(e) => (config.production && !isBackoffice ? e.preventDefault() : e)}
          errMsg={debouncedEmailConfirmError}
          labelAbove
        />
      )}
    />
  );

  const renderEmailFields = () => (
    <>
      {renderEmailField()}
      {renderEmailConfirmField()}
    </>
  );

  const renderPhoneFields = () => (
    <>
      <Controller
        name="phone"
        control={control}
        render={({ field }) => (
          <Input
            label="Numero de celular"
            id="phone"
            {...field}
            onChange={(e) => field.onChange(e.target.value.replace(/\s/g, ''))}
            errMsg={formState.errors.phone?.message}
            labelAbove
            type="phone"
          />
        )}
      />
      <Controller
        name="phoneConfirm"
        control={control}
        render={({ field }) => (
          <Input
            label="Confirmar numero de celular"
            id="phoneConfirm"
            {...field}
            onChange={(e) => field.onChange(e.target.value.replace(/\s/g, ''))}
            errMsg={formState.errors.phoneConfirm?.message}
            labelAbove
            type="phone"
          />
        )}
      />
    </>
  );

  if (showPhoneOption) {
    return (
      <div className="glass-surface flex flex-col items-center self-stretch p-3">
        <div className="flex flex-col gap-4 items-start self-stretch">
          <h1 className="text-lg font-semibold">Contacto</h1>
          <div className="flex flex-col gap-4 w-full">
            <Controller
              name="contactMethod"
              control={control}
              render={({ field }) => (
                <>
                  <RadioButton
                    name="contactMethod"
                    value="email"
                    checked={field.value === 'email'}
                    description="Recibirás las entradas en tu correo electrónico."
                    label="Correo electrónico"
                    disabled={disabled}
                    onChange={() => {
                      field.onChange('email');
                      setValue('phone', '');
                      setValue('phoneConfirm', '');
                      clearErrors(['phone', 'phoneConfirm']);
                    }}
                  />
                  <RadioButton
                    name="contactMethod"
                    value="phone"
                    checked={field.value === 'phone'}
                    description="Recibirás las entradas por WhatsApp."
                    label="Número de celular"
                    disabled={disabled}
                    onChange={() => {
                      // El correo se mantiene: se pide igual aunque las
                      // entradas vayan por WhatsApp, solo sin confirmación.
                      field.onChange('phone');
                      clearErrors('emailConfirm');
                    }}
                  />
                </>
              )}
            />
            {getValues('contactMethod') === 'phone' && (
              <>
                {renderPhoneFields()}
                {renderEmailField()}
              </>
            )}
            {getValues('contactMethod') === 'email' && renderEmailFields()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-surface flex flex-col items-center self-stretch p-3">
      <div className="flex flex-col gap-4 items-start self-stretch">
        <h1 className="text-lg font-semibold">Contacto</h1>
        <div className="flex flex-col gap-4 w-full">{renderEmailFields()}</div>
      </div>
    </div>
  );
};

export default CheckoutContactForm;
